// src/pages/WhiteboardPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { CanvasComponent } from '../components/Canvas/CanvasComponent';
import { Toolbar } from '../components/Canvas/Toolbar';
import { useClipboardCopy } from '../hooks/useClipboardCopy';
import { useClipboardPaste } from '../hooks/useClipboardPaste';
import { useClipboardCut } from '../hooks/useClipboardCut';
import { 
  DrawingState, 
  UIState, 
  Line, 
  TextNote,
  ImageElement,
  BackgroundType, 
  ToolType,
  LineStyle
} from '../components/Canvas/types';

const DEFAULT_OFFSET_X = 0;
const DEFAULT_OFFSET_Y = 0;
const DEFAULT_SCALE = 1;
const DEFAULT_LINE_WIDTH = 3;
const DEFAULT_COLOR = '#000000';
const DEFAULT_ERASER_SIZE = 30;

export const WhiteboardPage = () => {
  // Инициализация состояния
  const [backgroundType, setBackgroundType] = useState<BackgroundType>('grid');
  const [uiState, setUiState] = useState<UIState>({
    offsetX: DEFAULT_OFFSET_X,
    offsetY: DEFAULT_OFFSET_Y,
    scale: DEFAULT_SCALE,
    tool: 'pen',
    penColor: DEFAULT_COLOR,
    penWidth: DEFAULT_LINE_WIDTH,
    eraserSize: DEFAULT_ERASER_SIZE,
    lineStyle: 'solid',
    selection: [],
    isSelecting: false,
    isDraggingSelection: false
  });
  
  // Инициализация истории изменений
  const [history, setHistory] = useState<DrawingState[]>([{
    lines: [],
    textNotes: [],
    images: []
  }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  const [drawingState, setDrawingState] = useState<DrawingState>({
    lines: [],
    textNotes: [],
    images: []
  });
  
  // Добавлены недостающие состояния
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editingTextNote, setEditingTextNote] = useState<TextNote | null>(null);
  // Новое состояние для хранения цвета выделенных элементов
  const [selectionColor, setSelectionColor] = useState<string | null>(null);
  // Новое состояние для хранения ширины выделенных элементов
  const [selectionWidth, setSelectionWidth] = useState<number | null>(null);
  
  // Сохранение состояния в истории
  const saveToHistory = (newState: DrawingState) => {
    // Если мы находимся не в конце истории, обрезаем "лишнее"
    const newHistory = historyIndex < history.length - 1 
      ? history.slice(0, historyIndex + 1) 
      : [...history];
      
    // Добавляем новое состояние
    newHistory.push(newState);
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setDrawingState(newState);
  };
  
  // Обработчик для отмены
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setDrawingState(history[historyIndex - 1]);
    }
  };
  
  // Обработчик для повтора
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setDrawingState(history[historyIndex + 1]);
    }
  };
  
  // Обработчик добавления линии
  const handleAddLine = (line: Line) => {
    const newState = {
      ...drawingState,
      lines: [...drawingState.lines, line]
    };
    saveToHistory(newState);
  };
  
  // Обработчик удаления линии
  const handleDeleteLine = (lineId: string) => {
    const newState = {
      ...drawingState,
      lines: drawingState.lines.filter(line => line.id !== lineId)
    };
    saveToHistory(newState);
  };
  
  // Обработчик обновления линий и текстовых заметок
  const handleUpdateLines = (lines: Line[], textNotes: TextNote[], images: ImageElement[]) => {
    const newState = {
      lines,
      textNotes,
      images
    };
    saveToHistory(newState);
  };
  
  // Обработчик изменения состояния UI
  const handleUiStateChange = (newUiState: UIState) => {
    setUiState(newUiState);
  };
  
  // Обработчик изменения цвета
  const handleColorChange = (color: string) => {
    setUiState(prev => ({
      ...prev,
      penColor: color
    }));
  };
  
  // Обработчик изменения ширины
  const handleWidthChange = (width: number) => {
    setUiState(prev => ({
      ...prev,
      penWidth: width
    }));
    
    // Если есть выделенные элементы, обновляем их ширину
    if (uiState.selection.length > 0) {
      const newLines = drawingState.lines.map(line => 
        uiState.selection.includes(line.id) ? { ...line, width } : line
      );
      handleUpdateLines(newLines, drawingState.textNotes || [], drawingState.images || []);
    }
  };
  
  // Обработчик изменения стиля линии
  const handleLineStyleChange = (style: LineStyle) => {
    setUiState(prev => ({
      ...prev,
      lineStyle: style
    }));
  };
  
  // Обработчик изменения стиля линии для выделенных элементов
  const handleLineStylesChange = (style: LineStyle) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, lineStyle: style } : line
    );
    handleUpdateLines(newLines, drawingState.textNotes || [], drawingState.images || []);
  };
  
  // Обработчик изменения цвета для выделенных элементов
  const handleLineColorChange = (color: string) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, color } : line
    );
    const newTextNotes = (drawingState.textNotes || []).map(note => 
      uiState.selection.includes(note.id) ? { ...note, color } : note
    );
    handleUpdateLines(newLines, newTextNotes, drawingState.images || []);
    // Сохраняем цвет выделенных элементов
    setSelectionColor(color);
  };
  
  // Обработчик изменения ширины для выделенных элементов
  const handleLineWidthChange = (width: number) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, width } : line
    );
    handleUpdateLines(newLines, drawingState.textNotes || [], drawingState.images || []);
    // Сохраняем ширину выделенных элементов
    setSelectionWidth(width);
    
    // Обновляем общий penWidth, если все выделенные элементы имеют одинаковую ширину
    const selectedLines = drawingState.lines.filter(line => uiState.selection.includes(line.id));
    const allSameWidth = selectedLines.every(line => line.width === width);
    if (allSameWidth) {
      setUiState(prev => ({
        ...prev,
        penWidth: width
      }));
    }
  };
  
  // Обработчик изменения фона
  const handleBackgroundChange = (type: BackgroundType) => {
    setBackgroundType(type);
  };
  
  // Обработчик изменения инструмента
  const handleToolChange = (tool: ToolType) => {
    setUiState(prev => ({
      ...prev,
      tool
    }));
  };
  
  // Обработчик вставки из буфера обмена
  const handlePaste = (data: { lines?: Line[]; textNotes?: TextNote[]; images?: ImageElement[] }, center: { x: number; y: number }) => {
    if ((!data.lines || data.lines.length === 0) && 
        (!data.textNotes || data.textNotes.length === 0) &&
        (!data.images || data.images.length === 0)) return;
    
    const { offsetX, offsetY, scale } = uiState;
    const container = document.querySelector('.canvas-container');
    const canvas = document.querySelector('canvas');
    
    if (!container || !canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    // Получаем координаты курсора относительно canvas
    const centerX = center.x - rect.left;
    const centerY = center.y - rect.top;
    
    // Вычисляем виртуальные координаты курсора
    const virtualX = (centerX + offsetX) / scale;
    const virtualY = (centerY + offsetY) / scale;
    
    // Создаем новые элементы
    const newLines = (data.lines || []).map(line => ({
      ...line,
      id: `pasted-${line.id}-${Date.now()}`,
      points: line.points.map(p => ({
        x: p.x + (virtualX - line.points[0].x),
        y: p.y + (virtualY - line.points[0].y)
      }))
    }));
    
    const newTextNotes = (data.textNotes || []).map(note => ({
      ...note,
      id: `pasted-${note.id}-${Date.now()}`,
      x: note.x + (virtualX - note.x),
      y: note.y + (virtualY - note.y)
    }));
    
    const newImages = (data.images || []).map(image => ({
      ...image,
      id: `pasted-${image.id}-${Date.now()}`,
      x: virtualX - image.width / 2,
      y: virtualY - image.height / 2
    }));
    
    const newState = {
      lines: [...drawingState.lines, ...newLines],
      textNotes: [...(drawingState.textNotes || []), ...newTextNotes],
      images: [...(drawingState.images || []), ...newImages]
    };
    
    saveToHistory(newState);
    
    // Выделяем вставленные элементы
    const selection = [
      ...newLines.map(l => l.id), 
      ...newTextNotes.map(n => n.id),
      ...newImages.map(i => i.id)
    ];
    
    setUiState(prev => ({
      ...prev,
      selection: selection
    }));
  };
  
  // Получить bounding box для выделения
  const getSelectionBoundingBox = (state: DrawingState, selection: string[]) => {
    if (selection.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    
    for (const id of selection) {
      const line = state.lines.find(l => l.id === id);
      if (line) {
        for (const point of line.points) {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        }
      }
      const note = state.textNotes?.find(n => n.id === id);
      if (note) {
        minX = Math.min(minX, note.x);
        minY = Math.min(minY, note.y);
        maxX = Math.max(maxX, note.x + note.width);
        maxY = Math.max(maxY, note.y + note.height);
      }
      const image = state.images?.find(i => i.id === id);
      if (image) {
        minX = Math.min(minX, image.x);
        minY = Math.min(minY, image.y);
        maxX = Math.max(maxX, image.x + image.width);
        maxY = Math.max(maxY, image.y + image.height);
      }
    }
    
    if (minX === Infinity) return null;
    
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  };
  
  // Обработчик клавиатуры для удаления через Delete и Backspace
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Обработка Shift
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
      
      // Обработка Escape
      if (e.key === 'Escape' && isEditingText) {
        setIsEditingText(false);
        setEditingTextNote(null);
      }
      
      // Обработка Delete и Backspace для удаления выделенных элементов
      if ((e.key === 'Delete' || e.key === 'Backspace') && uiState.selection.length > 0 && !isEditingText) {
        e.preventDefault();
        const newState = {
          lines: drawingState.lines.filter(line => !uiState.selection.includes(line.id)),
          textNotes: (drawingState.textNotes || []).filter(note => !uiState.selection.includes(note.id)),
          images: (drawingState.images || []).filter(image => !uiState.selection.includes(image.id))
        };
        saveToHistory(newState);
        setUiState(prev => ({ ...prev, selection: [] }));
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [uiState.selection, drawingState, saveToHistory, isEditingText]);
  
  // Эффект для обновления цвета выделенных элементов
  useEffect(() => {
    if (uiState.selection.length === 0) {
      setSelectionColor(null);
      setSelectionWidth(null);
      return;
    }
    
    const selectedLines = drawingState.lines.filter(line => uiState.selection.includes(line.id));
    const selectedNotes = (drawingState.textNotes || []).filter(note => uiState.selection.includes(note.id));
    
    if (selectedLines.length + selectedNotes.length === 0) {
      setSelectionColor(null);
      setSelectionWidth(null);
      return;
    }
    
    // Получаем цвет первого элемента
    const firstLineColor = selectedLines.length > 0 ? selectedLines[0].color : null;
    const firstNoteColor = selectedNotes.length > 0 ? selectedNotes[0].color : null;
    const firstColor = firstLineColor || firstNoteColor;
    
    // Проверяем, все ли элементы имеют одинаковый цвет
    const allSameColor = selectedLines.every(line => line.color === firstColor) && 
                        selectedNotes.every(note => note.color === firstColor);
    
    setSelectionColor(allSameColor ? firstColor : null);
    
    // Получаем ширину первого элемента (только для линий)
    if (selectedLines.length > 0) {
      const firstWidth = selectedLines[0].width;
      const allSameWidth = selectedLines.every(line => line.width === firstWidth);
      setSelectionWidth(allSameWidth ? firstWidth : null);
    } else {
      setSelectionWidth(null);
    }
  }, [uiState.selection, drawingState]);
  
  // Подключаем хуки для работы с буфером обмена
  useClipboardCopy(uiState.selection, drawingState);
  useClipboardPaste(handlePaste);
  useClipboardCut(
    uiState.selection,
    drawingState,
    handleUpdateLines
  );
  
  return (
    <div className="app">
      <Toolbar
        currentTool={uiState.tool}
        onToolChange={handleToolChange}
        penColor={uiState.penColor}
        onColorChange={handleColorChange}
        penWidth={uiState.penWidth}
        onWidthChange={handleWidthChange}
        lineStyle={uiState.lineStyle}
        onLineStyleChange={handleLineStyleChange}
        onLineStylesChange={handleLineStylesChange}
        backgroundType={backgroundType}
        onBackgroundChange={handleBackgroundChange}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onUndo={handleUndo}
        onRedo={handleRedo}
        selectedLines={uiState.selection}
        onLineColorChange={handleLineColorChange}
        onLineWidthChange={handleLineWidthChange}
        // Передаем цвет выделенных элементов в Toolbar
        selectionColor={selectionColor}
        // Передаем ширину выделенных элементов в Toolbar
        selectionWidth={selectionWidth}
      />
      <div className="canvas-area">
        <CanvasComponent
          backgroundType={backgroundType}
          drawingState={drawingState}
          uiState={uiState}
          onUiStateChange={handleUiStateChange}
          onAddLine={handleAddLine}
          onDeleteLine={handleDeleteLine}
          onUpdateLines={handleUpdateLines}
        />
      </div>
    </div>
  );
};